import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ErrorState,
  Input,
  Label,
  Loading,
} from '@attendance/ui';
import { Check, X } from 'lucide-react';
import { appLockApi } from '../../api/settings.api.js';
import { friendlyMessage } from '../../lib/errors.js';

const PIN_RE = /^\d{4,8}$/;

/**
 * THE KIOSK LOCK, AND THE ONE PIN OF THE TWO THAT IS SET HERE.
 *
 * There are still two layers. There is only one CONTROL, because the two PINs
 * are set in two different places and always were — the owner, twice, in their
 * own words: "app pin code set in settings panel — but settings pin is set from
 * admin settings on server", and then "one password for settings needed, other
 * pin is from settings inside apk".
 *
 *   APP PIN    opens the app each morning on a shared tablet. The gate staff
 *              who use it rotate it themselves, from the field app's own
 *              Settings (`apps/field/.../LockGroup.tsx`), proving the admin PIN
 *              as they do it. This screen carried a second box for it, so the
 *              same secret had two homes and the console's copy could silently
 *              overwrite what a site had just set. Its STATUS stays — the
 *              enable switch below depends on it — as a fact, not a control.
 *   ADMIN PIN  opens Settings inside the field app, where a device is
 *              re-pointed at another server, its worksite rebound, its gallery
 *              wiped. There is no route by which a device can change it (see
 *              `field.appLock.routes.ts`), which is what makes the asymmetry
 *              coherent rather than arbitrary: only somebody holding this PIN
 *              can reach the screen that changes the everyday one.
 *
 * The wording is the field app's own — "App PIN", "Admin PIN" (§3.6). It read
 * "Open PIN" and "Admin PIN (Kiosk Devices)" here and "App PIN" / "Admin PIN"
 * on the tablet, which is one lock described in two vocabularies to the two
 * people who have to talk to each other about it over a phone.
 *
 * PINs are write-only — the server stores bcrypt hashes and only ever reports
 * whether each is set.
 */
export function AppLockSettingsSection(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const {
    data,
    isLoading,
    isError: loadFailed,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['app-lock', 'status'],
    queryFn: appLockApi.status,
  });

  const [enabled, setEnabled] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  // A toast is not a record. This page reported a failed save ONLY through
  // `toast.error`, so once it faded the screen said nothing had gone wrong —
  // while the PIN boxes still held the values that were never stored. The
  // persistent message below the form is where 16 of the app's 22 forms put it.
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setEnabled(data.enabled);
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      appLockApi.configure({
        enabled,
        ...(adminPin ? { adminPin } : {}),
      }),
    onSuccess: (status) => {
      qc.setQueryData(['app-lock', 'status'], status);
      void qc.invalidateQueries({ queryKey: ['app-lock', 'status'] });
      setAdminPin('');
      setSaveError(null);
      toast.success(t('settings.appLock.saveSuccess', 'Kiosk lock updated'));
    },
    onError: (err) => {
      const message = friendlyMessage(err);
      setSaveError(message);
      toast.error(message);
    },
  });

  const adminPinInvalid = adminPin !== '' && !PIN_RE.test(adminPin);
  /* The server refuses to enable a lock with no app PIN behind it, and this
     screen can no longer offer the fix inline — the app PIN is set on a device.
     So the switch is blocked here too, rather than sending a save that comes
     back as a validation error the reader cannot act on from this page. */
  const enablingWithoutPin = enabled && !data?.hasOpenPin;
  const canSave = !save.isPending && !adminPinInvalid && !enablingWithoutPin;

  /*
   * THREE STATES, NOT TWO — and the third one was rendering as the first.
   *
   * The guard here was `isLoading || !data`, which collapses "still fetching"
   * and "the fetch failed" into one branch: on a 500 `isLoading` goes false and
   * `data` stays `undefined`, so the panel sat on "Loading…" forever with no
   * error, no retry and nothing to say the request had come back at all. That is
   * the same shape as the `data?.length === 0` defect this app has shipped
   * repeatedly — an unresolved query wearing a confident answer — except the
   * confident answer here is "any moment now".
   *
   * Failed is checked FIRST, because `isLoading` is false by then, and it offers
   * the retry. Loading is the `Loading` primitive rather than a hand-written
   * paragraph, so it announces like every other loading state in the app.
   */
  if (loadFailed) {
    return (
      <Card>
        <ErrorState
          title={t('settings.appLock.loadError.title', "Couldn't load the kiosk lock settings")}
          description={`${
            friendlyMessage(loadError) ||
            t('common.checkConnection')
          } ${t(
            'settings.appLock.loadError.unchanged',
            'Nothing has been changed, and the lock is still whatever it was.',
          )}`}
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              {t('settings.appLock.loadError.retry', 'Try again')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card>
        <Loading label={t('settings.appLock.loading', 'Loading kiosk lock settings…')} />
      </Card>
    );
  }

  return (
    /* A LANDMARK, not a loose card. This used to be wrapped on the Settings
       page by a block that printed "Kiosk lock" a second time above it, plus a
       sentence explaining that the page's foot bar would not save it. Both are
       gone: the heading below is the only one, the section element carries the
       landmark the wrapper used to, and the button names its own scope. */
    <section
      id="settings-section-appLock"
      aria-labelledby="settings-section-appLock-title"
    >
      <Card>
        <CardHeader>
          <CardTitle id="settings-section-appLock-title">
            {t('settings.appLock.title', 'Kiosk lock')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.appLock.description',
              'Two PINs guard a shared kiosk tablet. The app PIN opens the app and is set on the device; the admin PIN opens Settings and is set here.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-component">
          <Checkbox
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            label={t('settings.appLock.enableLabel', 'Lock the app on this and other devices')}
            description={t(
              'settings.appLock.enableDescription',
              'When on, the app shows the PIN pad on launch until the app PIN is entered.',
            )}
          />

          <div className="grid gap-component sm:grid-cols-2">
            <PinField
              id="admin-pin"
              label={t('settings.appLock.adminPin.label', 'Admin PIN')}
              help={t(
                'settings.appLock.adminPin.help',
                'Opens Settings inside the field app. This is the only place it is set.',
              )}
              isSet={data.hasAdminPin}
              value={adminPin}
              invalid={adminPinInvalid}
              onChange={setAdminPin}
              t={t}
            />
            {/* THE OTHER PIN, AS A FACT RATHER THAN A BOX. It is set on the
                device and this page must not offer a second way to set it, but
                dropping the row entirely would take the answer with it — the
                switch above cannot be turned on until this says Set, and a
                blocked control whose obstacle is invisible is §3.11 backwards. */}
            <div className="space-y-element">
              <div className="flex items-center justify-between">
                <span className="text-label font-medium text-foreground">
                  {t('settings.appLock.appPin.label', 'App PIN')}
                </span>
                <SetMark isSet={data.hasOpenPin} t={t} />
              </div>
              <p className="text-copy-sm text-muted-foreground">
                {t(
                  'settings.appLock.appPin.managed',
                  'Set on the device, in the field app’s Settings. Changing it there needs the admin PIN.',
                )}
              </p>
            </div>
          </div>

          {/* The obstacle, named, next to the control it blocks — and it names
              the SCREEN that fixes it, which is not this one. "Set an open PIN
              before enabling the lock" was true and useless once the box that
              set it left this page. */}
          {enablingWithoutPin && (
            <p className="text-copy text-warning-on-surface">
              {t(
                'settings.appLock.enableRequiresPin',
                'No app PIN has been set yet. Set one on a device, in the field app’s Settings, before switching the lock on.',
              )}
            </p>
          )}

          {saveError && (
            <p className="text-copy text-danger" role="alert">
              {saveError}
            </p>
          )}

          {/* Submit cluster: end of the column, primary trailing. */}
          <div className="flex flex-wrap justify-end gap-element">
            <Button onClick={() => save.mutate()} disabled={!canSave} aria-busy={save.isPending}>
              {save.isPending
                ? t('settings.appLock.saving', 'Saving…')
                : t('settings.appLock.save', 'Save kiosk lock')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * "Set" or "Not set", with a tick or a cross — so the two are never told apart
 * by the green alone. `-on-surface` is the ink that clears AA on a card; the raw
 * `text-success` hue does not at this size.
 *
 * Its own component because BOTH PINs report it and only one of them has a box:
 * one mark, one wording, whether or not the row beneath it can be typed into.
 */
function SetMark({ isSet, t }: { isSet: boolean; t: TFunction }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-tight text-label ${isSet ? 'text-success-on-surface' : 'text-muted-foreground'}`}
    >
      {isSet ? <Check size={13} /> : <X size={13} />}
      {isSet ? t('settings.appLock.pinSet', 'Set') : t('settings.appLock.pinNotSet', 'Not set')}
    </span>
  );
}

function PinField({
  id,
  label,
  help,
  isSet,
  value,
  invalid,
  onChange,
  t,
}: {
  id: string;
  label: string;
  /** What this PIN opens, and where else it could be set. */
  help: string;
  isSet: boolean;
  value: string;
  invalid: boolean;
  onChange: (v: string) => void;
  t: TFunction;
}): JSX.Element {
  return (
    <div className="space-y-element">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        <SetMark isSet={isSet} t={t} />
      </div>
      <Input
        id={id}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={8}
        placeholder={
          isSet
            ? t('settings.appLock.pinPlaceholderChange', 'Enter a new PIN to change')
            : t('settings.appLock.pinPlaceholderSet', 'Set a 4–8 digit PIN')
        }
        value={value}
        // `error` derives `aria-invalid`; the describedby is what turns
        // "invalid" into "PIN must be 4–8 digits".
        error={invalid}
        aria-describedby={invalid ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      />
      <p className="text-copy-sm text-muted-foreground">{help}</p>
      {invalid && (
        <p id={`${id}-error`} className="text-copy-sm text-danger" role="alert">
          {t('settings.appLock.pinInvalid', 'PIN must be 4–8 digits.')}
        </p>
      )}
    </div>
  );
}
